import { useMemo, useState } from "react";
import {
  CheckCircle2,
  Download,
  ImageDown,
  Loader2,
  MapPin,
  PackageCheck,
  Search,
  ShieldCheck,
  Store,
} from "lucide-react";
import {
  searchProduct,
  type StoreProductResult,
} from "../../services/ProductSearchService";

const CAPTURE_WIDTH = 430;
const CAPTURE_HEIGHT = 760;

interface CaptureDetails {
  product: StoreProductResult;
  itemId: string;
  checkedAt: Date;
}

function money(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
}

function referencePrice(product: StoreProductResult): number {
  return Math.max(product.msrp || 0, product.onlinePrice || 0);
}

function discountFor(product: StoreProductResult) {
  const reference = referencePrice(product);
  const savings = Math.max(0, reference - product.storePrice);
  const percent = reference > 0 ? Math.round((savings / reference) * 100) : 0;
  return { reference, savings, percent };
}

function displayAddress(product: StoreProductResult): string {
  return product.storeAddress?.trim() || `Walmart store #${product.storeId}`;
}

function wrapCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth) {
      current = candidate;
      continue;
    }
    if (current) lines.push(current);
    current = word;
    if (lines.length === maxLines - 1) break;
  }
  if (current && lines.length < maxLines) lines.push(current);

  if (words.join(" ") !== lines.join(" ") && lines.length) {
    let finalLine = lines[lines.length - 1];
    while (finalLine.length > 1 && context.measureText(`${finalLine}…`).width > maxWidth) {
      finalLine = finalLine.slice(0, -1);
    }
    lines[lines.length - 1] = `${finalLine.trim()}…`;
  }
  return lines;
}

async function loadCanvasImage(src: string): Promise<HTMLImageElement | null> {
  if (!src) return null;
  try {
    const response = await fetch(src, { mode: "cors" });
    if (!response.ok) return null;
    const objectUrl = URL.createObjectURL(await response.blob());
    const image = new Image();
    const loaded = await new Promise<HTMLImageElement | null>((resolve) => {
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
    return loaded;
  } catch {
    return null;
  }
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: string,
) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.fillStyle = fill;
  context.fill();
}

async function renderCapture(details: CaptureDetails): Promise<HTMLCanvasElement> {
  const scale = 2;
  const canvas = document.createElement("canvas");
  canvas.width = CAPTURE_WIDTH * scale;
  canvas.height = CAPTURE_HEIGHT * scale;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Your browser could not create the image.");
  context.scale(scale, scale);

  const { product, itemId, checkedAt } = details;
  const discount = discountFor(product);
  const priceParts = money(product.storePrice).replace("$", "").split(".");
  const [brandMark, productImage] = await Promise.all([
    loadCanvasImage("/hawk_icon_white.svg"),
    loadCanvasImage(product.image),
  ]);

  context.fillStyle = "#f5f3f6";
  context.fillRect(0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);

  context.fillStyle = "#3b085f";
  context.fillRect(0, 0, CAPTURE_WIDTH, 132);
  if (brandMark) context.drawImage(brandMark, 20, 22, 27, 27);
  context.fillStyle = "#ffffff";
  context.font = "700 20px Arial, sans-serif";
  context.fillText("DealHawk price check", 58, 42);
  context.font = "500 12px Arial, sans-serif";
  context.fillStyle = "#e7dbed";
  context.fillText("Live local price sourced from Walmart", 24, 72);
  context.fillStyle = "#ffffff";
  context.font = "600 13px Arial, sans-serif";
  context.fillText(`Store #${product.storeId}`, 24, 105);
  context.font = "400 12px Arial, sans-serif";
  const address = displayAddress(product);
  context.fillText(address.length > 52 ? `${address.slice(0, 49)}…` : address, 24, 122);

  roundedRect(context, 16, 150, 398, 564, 18, "#ffffff");

  roundedRect(context, 90, 174, 250, 218, 14, "#f7f8fa");
  if (productImage) {
    const ratio = Math.min(218 / productImage.naturalHeight, 250 / productImage.naturalWidth);
    const width = productImage.naturalWidth * ratio;
    const height = productImage.naturalHeight * ratio;
    context.drawImage(productImage, 90 + (250 - width) / 2, 174 + (218 - height) / 2, width, height);
  } else {
    context.fillStyle = "#8b96a3";
    context.font = "600 13px Arial, sans-serif";
    context.textAlign = "center";
    context.fillText("Product image unavailable", 215, 286);
    context.textAlign = "left";
  }

  if (product.clearance || discount.percent > 0) {
    roundedRect(context, 28, 409, 88, 25, 13, "#f1eaf5");
    context.fillStyle = "#3b085f";
    context.font = "700 11px Arial, sans-serif";
    context.fillText(product.clearance ? "CLEARANCE" : "LOCAL DEAL", 41, 426);
  }

  context.fillStyle = "#1f2937";
  context.font = "600 16px Arial, sans-serif";
  const titleLines = wrapCanvasText(context, product.name || "Walmart product", 370, 2);
  titleLines.forEach((line, index) => context.fillText(line, 28, 463 + index * 21));

  context.fillStyle = "#15191e";
  context.font = "700 44px Arial, sans-serif";
  const dollarLabel = `$${priceParts[0]}`;
  const dollarWidth = context.measureText(dollarLabel).width;
  context.fillText(dollarLabel, 28, 548);
  context.font = "700 21px Arial, sans-serif";
  context.fillText(priceParts[1] || "00", 28 + dollarWidth + 2, 525);

  if (discount.savings > 0) {
    context.fillStyle = "#5f6b76";
    context.font = "400 13px Arial, sans-serif";
    context.fillText(`Was ${money(discount.reference)}`, 28, 572);
    roundedRect(context, 253, 512, 140, 48, 10, "#eee4f3");
    context.fillStyle = "#3b085f";
    context.font = "700 13px Arial, sans-serif";
    context.fillText(`Save ${money(discount.savings)}`, 268, 533);
    context.font = "600 12px Arial, sans-serif";
    context.fillText(`${discount.percent}% off`, 268, 550);
  }

  roundedRect(context, 28, 594, 374, 82, 12, "#f4f0f6");
  context.fillStyle = "#3b085f";
  context.font = "700 13px Arial, sans-serif";
  context.fillText("Pickup at this store", 46, 619);
  context.fillStyle = "#344252";
  context.font = "500 12px Arial, sans-serif";
  const inventory = product.inventory > 0
    ? `${product.inventoryExact ? "" : "About "}${product.inventory} in stock`
    : "Inventory not confirmed";
  context.fillText(inventory, 46, 642);
  const aisle = product.aisles?.trim() ? ` • Aisle ${product.aisles.trim()}` : "";
  context.fillText(`Item ${itemId}${aisle}`, 46, 660);

  context.fillStyle = "#65717d";
  context.font = "500 10px Arial, sans-serif";
  context.textAlign = "center";
  context.fillText(
    `Checked ${checkedAt.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}`,
    CAPTURE_WIDTH / 2,
    740,
  );
  context.textAlign = "left";

  return canvas;
}

function downloadCanvas(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("The PNG could not be generated."));
        return;
      }
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(href);
      resolve();
    }, "image/png");
  });
}

function PricePreview({ details }: { details: CaptureDetails }) {
  const { product, itemId, checkedAt } = details;
  const discount = discountFor(product);
  const [dollars, cents = "00"] = money(product.storePrice).replace("$", "").split(".");
  const inventory = product.inventory > 0
    ? `${product.inventoryExact ? "" : "About "}${product.inventory} in stock`
    : "Inventory not confirmed";

  return (
    <div
      aria-label="DealHawk price mockup preview"
      className="mx-auto w-full max-w-[430px] overflow-hidden rounded-[24px] bg-[#f5f3f6] text-[#1d191a] shadow-xl ring-1 ring-black/10"
    >
      <div className="bg-[#3b085f] px-6 pb-4 pt-6 text-white">
        <div className="flex items-center gap-3">
          <img
            src="/hawk_icon_white.svg"
            alt="DealHawk"
            className="h-7 w-7 shrink-0 object-contain"
          />
          <p className="text-xl font-bold tracking-tight">DealHawk price check</p>
        </div>
        <p className="mt-4 text-xs font-medium text-[#e7dbed]">Live local price sourced from Walmart</p>
        <div className="mt-3 flex items-start gap-2 text-xs">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-bold">Store #{product.storeId}</p>
            <p className="mt-0.5 text-[#e7dbed]">{displayAddress(product)}</p>
          </div>
        </div>
      </div>

      <div className="m-4 rounded-2xl bg-white p-3 shadow-sm">
        <div className="flex h-[218px] items-center justify-center overflow-hidden rounded-xl bg-[#f7f8fa]">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="h-full w-full object-contain mix-blend-multiply"
            />
          ) : (
            <ImageDown className="h-10 w-10 text-slate-300" />
          )}
        </div>

        {(product.clearance || discount.percent > 0) && (
          <span className="mt-4 inline-flex rounded-full bg-[#f1eaf5] px-3 py-1 text-[11px] font-extrabold tracking-wide text-[#3b085f]">
            {product.clearance ? "CLEARANCE" : "LOCAL DEAL"}
          </span>
        )}
        <h3 className="mt-3 line-clamp-2 text-base font-semibold leading-snug">
          {product.name || "Walmart product"}
        </h3>

        <div className="mt-3 flex items-end justify-between gap-3">
          <div>
            <div className="flex items-start font-bold leading-none">
              <span className="text-[44px] tracking-[-0.04em]">${dollars}</span>
              <span className="mt-1 text-xl">{cents}</span>
            </div>
            {discount.savings > 0 && (
              <p className="mt-1 text-xs text-slate-500">Was {money(discount.reference)}</p>
            )}
          </div>
          {discount.savings > 0 && (
            <div className="mb-1 min-w-[140px] rounded-xl bg-[#eee4f3] px-4 py-2 text-[#3b085f]">
              <p className="text-sm font-bold">Save {money(discount.savings)}</p>
              <p className="text-xs font-semibold">{discount.percent}% off</p>
            </div>
          )}
        </div>

        <div className="mt-5 rounded-xl bg-[#f4f0f6] p-4">
          <div className="flex items-center gap-2 text-sm font-bold text-[#3b085f]">
            <PackageCheck className="h-4 w-4" /> Pickup at this store
          </div>
          <p className="mt-2 text-xs font-medium text-slate-700">{inventory}</p>
          <p className="mt-1 text-xs text-slate-600">
            Item {itemId}{product.aisles?.trim() ? ` • Aisle ${product.aisles.trim()}` : ""}
          </p>
        </div>
      </div>

      <div className="px-4 pb-5 pt-1 text-center">
        <p className="text-[10px] font-medium text-slate-500">
          Checked {checkedAt.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
        </p>
      </div>
    </div>
  );
}

export default function PriceCaptureTab() {
  const [itemId, setItemId] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [requestedStoreId, setRequestedStoreId] = useState("");
  const [results, setResults] = useState<StoreProductResult[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [resolvedItemId, setResolvedItemId] = useState("");
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const selectedProduct = useMemo(
    () => results.find((result) => result.storeId === selectedStoreId) || results[0],
    [results, selectedStoreId],
  );

  const details = selectedProduct && checkedAt
    ? { product: selectedProduct, itemId: resolvedItemId, checkedAt }
    : null;

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedItemId = itemId.trim();
    const normalizedZip = zipCode.trim();
    if (!normalizedItemId || !normalizedZip) {
      setError("Enter both a Walmart item ID and ZIP code.");
      return;
    }

    setIsSearching(true);
    setError("");
    setNotice("");
    try {
      const response = await searchProduct(
        normalizedItemId,
        "SKU",
        normalizedZip,
        "walmart",
        { bypassCache: true },
      );
      const usable = response.results.filter((result) => !result.error && result.storePrice > 0);
      if (!usable.length) {
        throw new Error("No current store price was returned for that item near this ZIP code.");
      }

      const requested = requestedStoreId.trim();
      const match = requested ? usable.find((result) => result.storeId === requested) : usable[0];
      setResults(usable);
      setSelectedStoreId(match?.storeId || usable[0].storeId);
      setResolvedItemId(normalizedItemId);
      setCheckedAt(new Date());
      if (requested && !match) {
        setNotice(`Store #${requested} was not in the nearby results. Showing store #${usable[0].storeId}; choose another result below.`);
      }
    } catch (searchError) {
      setResults([]);
      setResolvedItemId("");
      setCheckedAt(null);
      setError(searchError instanceof Error ? searchError.message : "Price lookup failed.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleDownload = async () => {
    if (!details) return;
    setIsDownloading(true);
    setError("");
    try {
      const canvas = await renderCapture(details);
      await downloadCanvas(
        canvas,
        `dealhawk-price-${details.itemId}-store-${details.product.storeId}.png`,
      );
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "The PNG could not be generated.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-5 flex items-start gap-3">
        <div className="rounded-xl bg-[#eee4f3] p-2.5 text-[#3b085f] dark:bg-[#3b085f]/50 dark:text-[#e7dbed]">
          <ImageDown className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Store price capture</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-dark-300">
            Fetch a fresh Walmart store price, choose the store, then export a screenshot-ready DealHawk price mockup.
          </p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_430px] xl:items-start">
        <div className="space-y-5">
          <form
            onSubmit={handleSearch}
            className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-700 dark:bg-dark-900"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-dark-300">Walmart item ID</span>
                <input
                  value={itemId}
                  onChange={(event) => setItemId(event.target.value)}
                  inputMode="numeric"
                  placeholder="e.g. 1441822157"
                  className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition focus:border-[#3b085f] focus:ring-2 focus:ring-[#3b085f]/20 dark:border-dark-700 dark:bg-dark-800 dark:text-white"
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-dark-300">ZIP code</span>
                <input
                  value={zipCode}
                  onChange={(event) => setZipCode(event.target.value)}
                  inputMode="numeric"
                  placeholder="e.g. 43004"
                  className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition focus:border-[#3b085f] focus:ring-2 focus:ring-[#3b085f]/20 dark:border-dark-700 dark:bg-dark-800 dark:text-white"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-dark-300">Store ID <span className="font-medium normal-case tracking-normal">(optional)</span></span>
                <input
                  value={requestedStoreId}
                  onChange={(event) => setRequestedStoreId(event.target.value)}
                  inputMode="numeric"
                  placeholder="Prefer a specific nearby store"
                  className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition focus:border-[#3b085f] focus:ring-2 focus:ring-[#3b085f]/20 dark:border-dark-700 dark:bg-dark-800 dark:text-white"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={isSearching}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#3b085f] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#2a0541] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {isSearching ? "Checking live price…" : "Get current price"}
            </button>
          </form>

          {error && (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          )}
          {notice && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-200">
              {notice}
            </div>
          )}

          {results.length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-700 dark:bg-dark-900">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">Choose the store shown</h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-dark-300">{results.length} priced nearby store{results.length === 1 ? "" : "s"} returned</p>
                </div>
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {results.map((result) => {
                  const active = result.storeId === selectedProduct?.storeId;
                  return (
                    <button
                      key={result.storeId}
                      type="button"
                      onClick={() => setSelectedStoreId(result.storeId)}
                      className={`rounded-xl border p-3 text-left transition ${active
                        ? "border-[#3b085f] bg-[#f4f0f6] ring-2 ring-[#3b085f]/15 dark:bg-[#3b085f]/30"
                        : "border-gray-200 hover:border-gray-300 dark:border-dark-700 dark:hover:border-dark-600"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-white">
                          <Store className="h-3.5 w-3.5" /> #{result.storeId}
                        </span>
                        <span className="text-sm font-extrabold text-emerald-700 dark:text-emerald-400">{money(result.storePrice)}</span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-dark-300">{displayAddress(result)}</p>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleDownload}
                disabled={isDownloading}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-black disabled:opacity-60 dark:bg-white dark:text-dark-900"
              >
                {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {isDownloading ? "Generating PNG…" : "Download PNG"}
              </button>
            </div>
          )}

          <div className="flex gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-700 dark:bg-dark-900/60">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-dark-300" />
            <p className="text-xs leading-relaxed text-gray-500 dark:text-dark-300">
              Every export keeps the DealHawk header, checked time, store ID, and retailer source for clear provenance.
            </p>
          </div>
        </div>

        <div className="xl:sticky xl:top-6">
          {details ? (
            <PricePreview details={details} />
          ) : (
            <div className="flex min-h-[520px] items-center justify-center rounded-[24px] border-2 border-dashed border-gray-200 bg-white/60 p-8 text-center dark:border-dark-700 dark:bg-dark-900/50">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#eee4f3] text-[#3b085f] dark:bg-[#3b085f]/50 dark:text-[#e7dbed]">
                  <ImageDown className="h-6 w-6" />
                </div>
                <p className="mt-4 text-sm font-bold text-gray-900 dark:text-white">Your price mockup will appear here</p>
                <p className="mx-auto mt-1 max-w-xs text-xs leading-relaxed text-gray-500 dark:text-dark-300">
                  Look up an item to populate the product, current local price, savings, inventory, aisle, and store details.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
